// src/pages/Admin/Coaching/CoachingSummary.jsx
import React, { useMemo } from "react";
import { FaCalendarAlt, FaClock } from "react-icons/fa";

const fmt = (h) =>
  Number(h || 0)
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1");

const monthKey = (dateLike) => {
  const d = new Date(dateLike);
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  return `${y}-${m}`; // clé triable
};

const labelFR = (ym) => {
  // "YYYY-MM" -> "Septembre 2025"
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  const raw = d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

export default function CoachingSummary({ events = [] }) {
  const { rows, total } = useMemo(() => {
    const acc = new Map(); // key: "YYYY-MM" -> hours sum
    for (const ev of events) {
      const key = monthKey(ev.start);
      const h = Number(ev?.AdherentEvent?.coach_hour || 0);
      acc.set(key, (acc.get(key) || 0) + h);
    }
    // tri ascendant par mois (ou desc si tu préfères)
    const rows = [...acc.entries()]
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([date, hours]) => ({ date, hours: Number(hours.toFixed(2)) }));
    const total = rows.reduce((s, r) => s + r.hours, 0);
    return { rows, total: Number(total.toFixed(2)) };
  }, [events]);

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
            <FaCalendarAlt />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Récapitulatif des heures de coaching par mois
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Somme des heures enregistrées par mois.
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-sm">
          <FaClock className="text-slate-400" />
          <span className="text-slate-600 dark:text-slate-300">
            Total : <strong>{fmt(total)} h</strong>
          </span>
        </div>
      </div>

      {/* Contenu */}
      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
          Aucun enregistrement pour l’instant.
        </div>
      ) : (
        <div className="p-3 sm:p-5">
          {/* Table desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                    Mois / Année
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                    Heures cumulées
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((r) => (
                  <tr key={r.date} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-3 text-sm text-slate-800 dark:text-slate-100">
                      {labelFR(r.date)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {fmt(r.hours)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-900 text-white">
                  <td className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">
                    Total
                  </td>
                  <td className="px-4 py-3 text-xs font-semibold">{fmt(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Cartes mobile */}
          <div className="md:hidden space-y-3">
            {rows.map((r) => (
              <div
                key={`card-${r.date}`}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 px-4 py-3 flex items-center justify-between"
              >
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  {labelFR(r.date)}
                </span>
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {fmt(r.hours)} h
                </span>
              </div>
            ))}
            <div className="pt-2 text-right text-xs text-slate-700 dark:text-slate-300">
              Total : <span className="font-bold">{fmt(total)} h</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
